(() => {
  if (customElements.get('tbs-cart-offer')) return;
  const root = window.Shopify?.routes?.root || '/';
  const offerCodes = new Set(['TB6-TRAVEL-PAIR', 'TB6-STYLE-PAIR', 'TB6-BATH-PAIR', 'TB6-PILLOW-2', 'TB6-FAN-2', 'TB6-SOAP-2']);
  let busy = false;
  const request = async (path, body) => {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || String(response.status));
    return result;
  };
  class CartOffer extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.config = JSON.parse(this.querySelector('[data-offer-config]').textContent);
      this.button = this.querySelector('[data-offer-button]');
      this.select = this.querySelector('[data-variant-select]');
      this.status = this.querySelector('[data-offer-status]');
      this.select.addEventListener('change', () => this.paintPrices());
      this.button.addEventListener('click', () => this.apply());
      request('cart.js').then(cart => this.paint(cart)).catch(() => {
        this.button.textContent = this.config.strings.unavailable;
      });
    }
    items(cart, product) {
      return cart.items.filter(item => item.product_id === product.id && !item.selling_plan_allocation);
    }
    quantity(items) {
      return items.reduce((sum, item) => sum + item.quantity, 0);
    }
    paint(cart, selectedId = this.select.value) {
      if (!this.isConnected) return;
      this.cart = cart;
      const { primary, companion, strings } = this.config;
      const a = this.items(cart, primary);
      const b = companion ? this.items(cart, companion) : [];
      this.a = a;
      this.b = b;
      const aq = this.quantity(a);
      const bq = this.quantity(b);
      const multi = this.dataset.kind === 'multi';
      if ((!aq && multi) || (!multi && !aq && !bq)) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const codes = cart.discount_codes || [];
      const applied = codes.some(discount => discount.code.toUpperCase() === this.dataset.code && discount.applicable);
      this.missing = multi ? (aq < 2 ? primary : null) : (!aq ? primary : (!bq ? companion : null));
      this.select.replaceChildren();
      if (this.missing) {
        const variants = this.missing.variants.filter(variant => variant.available);
        for (const variant of variants) this.select.add(new Option(variant.title, String(variant.id)));
        const firstExisting = (multi ? a : (!aq ? b : a))[0];
        if (multi && variants.some(v => v.id === firstExisting?.variant_id)) this.select.value = String(firstExisting.variant_id);
        if (variants.some(v => String(v.id) === selectedId)) this.select.value = selectedId;
        this.querySelector('[data-choice]').hidden = variants.length < 2 || applied;
        this.querySelector('[data-choice-label]').textContent = strings.choose.replace('[product]', this.missing.title);
        this.button.disabled = !variants.length;
      } else {
        this.querySelector('[data-choice]').hidden = true;
        this.button.disabled = false;
      }
      this.button.textContent = applied ? strings.applied : (this.missing ? strings.add : strings.apply);
      if (applied) this.button.disabled = true;
      this.querySelector('[data-price-row]').hidden = applied;
      this.paintPrices();
    }
    paintPrices() {
      if (!this.cart) return;
      const { primary, companion, strings } = this.config;
      const chosen = this.missing?.variants.find(v => String(v.id) === this.select.value);
      let original;
      let discounted;
      if (this.dataset.kind === 'multi') {
        original = this.a.reduce((sum, item) => sum + item.original_price * item.quantity, 0) + (chosen?.price || 0);
        discounted = original - Math.round(original * 0.05);
      } else {
        const aPrice = this.a[0]?.original_price ?? (this.missing?.id === primary.id ? chosen?.price : primary.variants.find(v => v.available)?.price);
        const bPrice = this.b[0]?.original_price ?? (this.missing?.id === companion.id ? chosen?.price : companion.variants.find(v => v.available)?.price);
        original = aPrice + bPrice;
        discounted = original - Math.round(bPrice * 0.1);
      }
      const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currency: this.cart.currency || 'AUD',
      }).format(cents / 100);
      this.querySelector('[data-price-label]').textContent = this.dataset.kind === 'multi' ? strings.twoTotal : strings.pairTotal;
      this.querySelector('[data-original-price]').textContent = money(original);
      this.querySelector('[data-offer-price]').textContent = money(discounted);
    }
    async apply() {
      if (busy || this.button.disabled) return;
      busy = true;
      let added = false;
      let previousCodes;
      let discountChanged = false;
      const { strings } = this.config;
      const checkoutButtons = [...document.querySelectorAll('[name="checkout"]')].filter(button => !button.disabled);
      checkoutButtons.forEach(button => { button.disabled = true; });
      document.querySelectorAll('tbs-cart-offer').forEach(card => card.setAttribute('aria-busy', 'true'));
      this.status.textContent = '';
      this.button.disabled = true;
      this.button.textContent = strings.working;
      try {
        const cart = await request('cart.js');
        this.paint(cart);
        if (this.hidden) throw new Error(strings.error);
        if (this.missing) {
          const variant = this.missing.variants.find(v => String(v.id) === this.select.value && v.available);
          if (!variant) throw new Error(strings.unavailable);
          await request('cart/add.js', { items: [{ id: variant.id, quantity: 1 }] });
          added = true;
        }
        previousCodes = (cart.discount_codes || []).map(item => item.code);
        const codes = [...new Set([...previousCodes.filter(code => !offerCodes.has(code.toUpperCase())), this.dataset.code])];
        discountChanged = true;
        const result = await request('cart/update.js', { discount: codes.join(',') });
        const active = result.discount_codes?.some(item => item.code.toUpperCase() === this.dataset.code && item.applicable);
        if (!active) {
          const restored = await request('cart/update.js', { discount: previousCodes.join(',') });
          discountChanged = false;
          this.paint(restored);
          this.status.textContent = added ? strings.addedError : strings.discountError;
          return;
        }
        window.location.assign(root + 'cart');
      } catch (error) {
        if (discountChanged && previousCodes) {
          try { await request('cart/update.js', { discount: previousCodes.join(',') }); } catch (_) {}
        }
        this.status.textContent = added ? strings.addedError : strings.error;
      } finally {
        busy = false;
        checkoutButtons.forEach(button => { if (button.isConnected) button.disabled = false; });
        document.querySelectorAll('tbs-cart-offer').forEach(card => card.removeAttribute('aria-busy'));
        if (this.isConnected) {
          try { this.paint(await request('cart.js')); } catch (_) { this.button.disabled = false; this.button.textContent = strings.apply; }
        }
      }
    }
  }
  customElements.define('tbs-cart-offer', CartOffer);
})();
